"use client";
import React from "react";
import { PageManifestoSchema } from "@/sanity/pages/PageManifestoTemplate";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import Footer from "@/components/global/partials/Footer";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableText } from "next-sanity";
import ChipSerif from "../global/ui/ChipSerif";
import Image from "@/helpers/Image";
import sig from "@/public/manifesto/ChristophRadecker.svg";
import stamp from "@/public/manifesto/stamp.png";
import ProblemSection from "../pageComponents/manifesto/Problem";
import DifferenceSection from "../pageComponents/manifesto/Difference";
import LostOportunitySection from "../pageComponents/manifesto/LostOportunity";
import ConclusionSection from "../pageComponents/manifesto/Conclusion";
import LastCTASection from "../global/sections/LastCTA";
import { motion } from "framer-motion";

export default function PageManifestoJSX({
  data,
}: {
  data: PageManifestoSchema;
}) {
  return (
    <>
      {data?.settings?.header && (
        <NavBarV2 headerData={data?.settings?.header} animate={true} />
      )}

      <section className="pt-[14rem] lg:pt-[22rem] mb-[5rem] lg:mb-[7rem] px-[2rem] lg:px-0">
        <div className="container flex relative z-10 flex-col items-center px-[3rem]">
          {data?.heroChip && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: 0,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              viewport={{ once: true }}
            >
              <ChipSerif text={data.heroChip} className="uppercase" />
            </motion.div>
          )}

          {data?.heroTitle && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: 0.2,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              viewport={{ once: true }}
              className="richtext w-[min(85rem,75%)]"
            >
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.founder && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: 0.5,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              viewport={{ once: true }}
              className="flex items-center gap-[1rem] mt-[1.5rem]"
            >
              <Image
                src={data.founder.icon}
                alt=""
                className="rounded-full !w-[4rem] shrink-0"
              />
              <p className="mb-0">{data.founder.name}</p>
            </motion.div>
          )}
        </div>
      </section>

      <div className="flex flex-col gap-[12rem] lg:gap-[15rem] px-[2rem] lg:px-0 mb-[12rem]">
        <section id="letter">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.3,
              delay: 0,
              ease: [0.25, 0.4, 0.25, 1],
            }}
            viewport={{ once: true }}
            className="w-[min(94.25rem,100%)] mx-auto bg-[#E6EDE9] rounded-[2rem] lg:rounded-[4rem] px-[2rem] lg:px-[7rem] py-[5rem] lg:py-[6rem] flex flex-col"
          >
            {data?.letterTexts && (
              <div
                className="richtext richtext-start w-full
                prose-h2:text-[4rem] lg:prose-h2:text-[4.5rem] prose-h2:mb-[3rem]
                prose-img:!w-[6.7rem] prose-img:my-0
                prose-h4:text-[2.375rem] prose-h4:text-[#103037] prose-h4:mt-0
                prose-h5:text-[2.25rem] prose-h5:text-[#103037]
                prose-hr:border-black/10
                [&_.font-gestura]:text-[3rem] [&_.font-gestura]:text-[#07171B] [&_.font-gestura]:font-bold [&_.font-gestura]:tracking-[-0.06rem] [&_.font-gestura]:leading-[112%]
                [&_.faded]:!text-[#07171B]/50"
              >
                <PortableText
                  value={data.letterTexts}
                  components={richTextComponent}
                />
              </div>
            )}

            <hr className="w-full border-t border-[#10303733] border-dashed mt-[4rem] mb-[2.5rem]" />

            <div className="flex justify-between items-end w-full">
              <div className="flex flex-col">
                <Image
                  src={sig}
                  alt="signature"
                  className="!w-[26rem] lg:!w-[26rem]"
                />
                <span>
                  {data.letterFounderText ?? " — The Founder of IQ Capital"}
                </span>
              </div>

              <Image
                src={stamp}
                alt="stamp"
                className="!w-[15rem] lg:!w-[17rem] rotate-[9.34deg]"
              />
            </div>
          </motion.div>
        </section>

        <ProblemSection data={data} />

        <LostOportunitySection data={data} />

        <DifferenceSection data={data} />

        <ConclusionSection data={data} />
      </div>

      <LastCTASection
        chip={data?.lastChip}
        text={data?.lastText}
        cta={data?.lastCTA}
      />

      {data?.settings?.footer && (
        <Footer
          data={data.settings.footer}
          slug={data?.settings?.slug?.current}
        />
      )}
    </>
  );
}
